import React, { useState, useEffect } from 'react';
import ProductSection from '../components/ProductSection';
import axios from "axios";
import config from '../config/config';

const ProductAdmin = ({ config: initialConfig = {}, slug = '', onSave, onClose }) => {
  const [preset, setPreset] = useState('classic-grid');
  const [title, setTitle] = useState('Featured Products');
  const [subtitle, setSubtitle] = useState('Discover our best-selling items');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [devMode, setDevMode] = useState(false);
  const [grid, setGrid] = useState({ mobileCols: 2, desktopCols: 4 });
  const [imageSize, setImageSize] = useState({ width: 400, height: 500 });
  const [cardHeight, setCardHeight] = useState(520);
  const [productLinkPattern, setProductLinkPattern] = useState('/products/{id}');
const [featuredBg, setFeaturedBg] = useState('');
  const [container, setContainer] = useState({
    padding: 'py-8 md:py-12',
    background: 'bg-white',
    featuredBg:'bg-gray-400',
    textColor:'#111827',
    textAlign:'left',
    activeBg:'bg-gray-900',
     fontSize: '2em',
         fontFamily: config.FONT_FAMILY,
         titlefontSize:'text-2xl',
        subtitlefontSize:'text-md',
         titlefontWeight:'text-2xl',
        subtitlefontWeight:'text-md',
    borderRadius:'10px',
    cardBackground:'bg-white',
    titleColor: '#111827',     // gray-900
    subtitleColor: '#4B5563',  // gray-600
    accentColor:'green',
  });

  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const token = localStorage.getItem("token");

  // Load saved config
  useEffect(() => {
    const loadProductSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullProductSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.productSettings || {});

        setPreset(dbData.preset || preset);
        setTitle(dbData.title || title);
        setSubtitle(dbData.subtitle || subtitle);
        if (dbData.products?.length > 0) {
          setProducts(dbData.products);
        }
        setGrid(dbData.grid || grid);
        setImageSize(dbData.imageSize || imageSize);
        setCardHeight(dbData.cardHeight || cardHeight);
        setProductLinkPattern(dbData.productLinkPattern || productLinkPattern);
        setDevMode(dbData.devMode || devMode);
        setContainer(dbData.customStyles?.container || container);
      } catch (error) {
        console.error("Failed to load product settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProductSettings();
  }, [token, slug]);

  // Fetch products & categories
  const fetchAllProductsAndCategories = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${config.API_URL}/api/pullProducts/all`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${config.API_URL}/api/pullCategories`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      setAllProducts(productsRes.data?.data || productsRes.data || []);
      setCategories(categoriesRes.data?.categories || categoriesRes.data?.newCategories || []);
    } catch (err) {
      console.error("Failed to fetch:", err);
    }
  };

  useEffect(() => {
    if (token) fetchAllProductsAndCategories();
  }, [token]);

  const currentConfig = {
    preset,
    title,
    subtitle: subtitle || null,
    products,
    customStyles: { container },
    grid,
    imageSize,
    cardHeight,
    productLinkPattern,
    devMode,
  };

  const saveProductSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug.includes('-')
        ? `/api/saveCustomSettings/Product/${slug}`
        : '/api/saveProductSettings';

      const response = await axios.post(
        config.API_URL + endpoint,
        { settings: currentConfig },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("Products section saved successfully! 🎉");
        if (onSave) onSave(currentConfig);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = (id) => {
    setProducts(products.filter(p => p.id !== id));
  };

  const moveProduct = (id, direction) => {
    const index = products.findIndex(p => p.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === products.length - 1)) return;
    const newProducts = [...products];
    [newProducts[index], newProducts[index + direction]] = [newProducts[index + direction], newProducts[index]];
    setProducts(newProducts);
  };


  const openAddProductsModal = () => {
    setSelectedCategoryId('all');
    setSelectedProductIds([]);
    setIsModalOpen(true);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };


 // Format currency (KES)
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const confirmAddProducts = () => {
    const newProducts = allProducts
      .filter(p => selectedProductIds.includes(p.id))
      .map(p => ({
        id: p.id,
        name: p.name,
        price: parseFloat(p.price) || 0,
        originalPrice: parseFloat(p.regular_price) || null,
        discount: p.promotion ? Math.round(((parseFloat(p.regular_price || p.price) - parseFloat(p.price)) / parseFloat(p.regular_price || p.price)) * 100) : null,
        rating: p.rating || 0,
        reviews: p.reviews_count || 0,
        image: p.image_url,
        badge: p.is_promotion_active ? "Promotion" : null,
        inStock: p.stock > 0,
      }));

    const existingIds = new Set(products.map(p => p.id));
    const filteredNew = newProducts.filter(p => !existingIds.has(p.id));

    setProducts(prev => [...prev, ...filteredNew]);
    setIsModalOpen(false);
  };



  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Products Section Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-8">
            <ProductSection config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Sidebar */}
      <div className="w-96 bg-white border-l overflow-y-auto shadow-2xl relative">
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Settings</h2>

          {/* Preset */}
          <div>
            <h3 className="font-medium mb-3">Layout Style</h3>
            <div className="grid gap-3">
              {['classic-grid', 'minimal-clean', 'carousel'].map(p => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`p-4 border rounded-lg text-left ${preset === p ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                >
                  <span className="font-medium capitalize">{p.replace('-', ' ')}</span>
                </button>
              ))}
            </div>
          </div>

           {/* Title & Subtitle */}
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Section Title" />
            <input value={subtitle} onChange={e => setSubtitle(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Subtitle (optional)" />
          </div>

          {/* Products in this section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Products in Section ({products.length})</h3>
              <button
                onClick={openAddProductsModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                + Add Products
              </button>
            </div>

            {products.map((product) => (
              <div key={product.id} className="border rounded-lg p-4 bg-gray-50 flex justify-between items-center">
                <div>
                  <p className="font-medium">{product.name}</p>
                  <p className="text-sm text-gray-600">{formatCurrency(product.price)}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => moveProduct(product.id, -1)}
                    disabled={products.findIndex(p => p.id === product.id) === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveProduct(product.id, 1)}
                    disabled={products.findIndex(p => p.id === product.id) === products.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeProduct(product.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Grid Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mobile Columns</label>
              <select
                value={grid.mobileCols}
                onChange={e => setGrid({ ...grid, mobileCols: Number(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              >
                {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Desktop Columns</label>
              <select
                value={grid.desktopCols}
                onChange={e => setGrid({ ...grid, desktopCols: Number(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              >
                {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {/* Image Dimensions */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block text-sm font-medium mb-1">Image Width (px)</label>
              <input
                type="number"
                value={imageSize.width}
                onChange={e => setImageSize({ ...imageSize, width: Number(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
                min="100"
                max="1200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image Height (px)</label>
              <input
                type="number"
                value={imageSize.height}
                onChange={e => setImageSize({ ...imageSize, height: Number(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
                min="100"
                max="1200"
              />
            </div>
          </div>

          {/* NEW: Card Height */}
          <div className="mt-6">
            <label className="block text-sm font-medium mb-1">Product Card Height (px)</label>
            <input
              type="number"
              value={cardHeight}
              onChange={e => setCardHeight(Number(e.target.value))}
              className="w-full border rounded-md px-3 py-2"
              min="300"
              max="900"
            />
            <p className="text-xs text-gray-500 mt-1">
              Sets the total height of each product card (image + content)
            </p>
          </div>

          {/* Product Link Pattern */}
          <div className="mt-6">
            <label className="block text-sm font-medium mb-1">Product Link Pattern</label>
            <input
              value={productLinkPattern}
              onChange={e => setProductLinkPattern(e.target.value)}
              className="w-full px-4 py-2 border rounded-md text-sm"
              placeholder="e.g. /products/{id}  or  /shop/{slug}"
            />
            <p className="text-xs text-gray-500 mt-1">
              Available: {'{id}'}, {'{slug}'}, {'{name}'}
            </p>
          </div>

          {/* Dev Mode – Enhanced with background image support */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-yellow-900">Dev Mode – Custom Styles</h3>
              <button 
                onClick={() => setDevMode(!devMode)} 
                className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4">
              <div>
                  <label className="block text-sm font-medium mb-1">Title & Subtitle Position</label>
                  <input value={container.textAlign} onChange={e => setContainer({ ...container, textAlign: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="py-12 md:py-16 px-4 md:px-8" placeholder='left ,right ,center' />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Container Padding (Tailwind)
                  </label>
                  <input
                    value={container.padding}
                    onChange={e => setContainer({ ...container, padding: e.target.value })}
                    placeholder="py-8 md:py-12 px-4 md:px-8"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cart Button Gradient/Background</label>
                  <input
                    value={container.featuredBg}
                    onChange={e => setContainer({ ...container, featuredBg: e.target.value })}
                    placeholder="from-purple-600 to-pink-600"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cart Button Gradient/Background(While Active)</label>
                  <input
                    value={container.activeBg}
                    onChange={e => setContainer({ ...container, activeBg: e.target.value })}
                    placeholder="from-purple-600 to-pink-600"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div className="mt-3 space-y-3">
                 <label>Title Styles</label>
                        <input type="color" value={container.titleColor} onChange={e => setContainer({ ...container, titleColor: e.target.value })} className="h-10 w-full rounded border" />
                        <select value={container.titlefontSize} onChange={e => setContainer({ ...container, titlefontSize: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                          <option value="text-4xl md:text-5xl">4XL-5XL</option>
                  <option value="text-5xl md:text-6xl">5XL-6XL</option>
                  <option value="text-6xl md:text-7xl">6XL-7XL</option>

                        </select>
                        <select value={container.titlefontWeight} onChange={e => setContainer({ ...container, titlefontWeight: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                          <option value="font-bold">Bold</option>
                  <option value="font-semibold">Semibold</option>
                  <option value="font-medium">Medium</option>
                        </select>
                      </div>


                       <div className="mt-3 space-y-3">
                       <label>SubTitle Styles</label>
                        <input type="color" value={container.subtitleColor} onChange={e => setContainer({ ...container, subtitleColor: e.target.value })} className="h-10 w-full rounded border" />
                        <select value={container.subtitlefontSize} onChange={e => setContainer({ ...container, subtitlefontSize: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                          <option value="text-xl">XL</option>
                          <option value="text-2xl">2XL</option>
                          <option value="text-3xl">3XL</option>
                        </select>
                        <select value={container.subtitlefontWeight} onChange={e => setContainer({ ...container, subtitlefontWeight: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                          <option value="font-bold">Bold</option>
                          <option value="font-semibold">Semibold</option>
                        </select>
                      </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Section Accent Color</label>
                  <input
                  type="color"
                    value={container.accentColor}
                    onChange={e => setContainer({ ...container, accentColor: e.target.value })}
                    placeholder="#111827 or text-gray-900"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Section Text Color</label>
                  <input
                  type="color"
                    value={container.textColor}
                    onChange={e => setContainer({ ...container, textColor: e.target.value })}
                    placeholder="#111827 or text-gray-900"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Section Text Font Size</label>
                  <input
                
                    value={container.fontSize}
                    onChange={e => setContainer({ ...container, fontSize: e.target.value })}
                    placeholder="10px , 2em"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Section Font Family</label>
                  <input
                 
                    value={container.fontFamily}
                    onChange={e => setContainer({ ...container, fontFamily: e.target.value })}
                    placeholder="Tw Cen MT"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Card Border Radius</label>
                  <input
                  type="number"
                  min="1"
                    value={container.borderRadius}
                    onChange={e => setContainer({ ...container, borderRadius: e.target.value })}
                    placeholder="10px"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Card Background</label>
                  <input
                    value={container.cardBackground}
                    onChange={e => setContainer({ ...container, cardBackground: e.target.value })}
                    placeholder="from-purple-600 to-pink-600"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background (class / color / gradient / image)
                  </label>
                  <input
                    value={container.background}
                    onChange={e => setContainer({ ...container, background: e.target.value })}
                    placeholder="bg-white OR #f8fafc OR url(https://...) OR linear-gradient(to right, #f3f4f6, #e5e7eb)"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Examples: bg-gray-50, #fefce8, url(https://example.com/bg.jpg)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Or Upload Background Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const formData = new FormData();
                      formData.append('image', file);

                      try {
                        const res = await axios.post(
                          `${config.API_URL}/api/upload-image`,
                          formData,
                          {
                            headers: {
                              'Content-Type': 'multipart/form-data',
                              Authorization: `Bearer ${token}`,
                            },
                          }
                        );

                        if (res.data.url) {
                          setContainer(prev => ({
                            ...prev,
                            background: `url(${res.data.url})`
                          }));
                          alert("Image uploaded and set as background!");
                        }
                      } catch (err) {
                        console.error(err);
                        alert("Image upload failed");
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title Color
                  </label>
                  <input
                  type="color"
                    value={container.titleColor}
                    onChange={e => setContainer({ ...container, titleColor: e.target.value })}
                    placeholder="#111827 or text-gray-900"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle Color
                  </label>
                  <input
                  type="color"
                    value={container.subtitleColor}
                    onChange={e => setContainer({ ...container, subtitleColor: e.target.value })}
                    placeholder="#4B5563 or text-gray-600"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 right-0 w-96 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveProductSettings}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-70 shadow"
          >
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>

     
      {/* Add Products Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-xl font-bold">Add Products to Section</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-800 text-2xl">×</button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Filter by Category</label>
                <select
                  value={selectedCategoryId}
                  onChange={e => setSelectedCategoryId(e.target.value)}
                  className="w-full md:w-64 border rounded-md px-3 py-2"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4">
                {allProducts
                  .filter(p => selectedCategoryId === 'all' || p.category_id === Number(selectedCategoryId))
                  .map(product => (
                    <div
                      key={product.id}
                      className={`p-4 border rounded-lg cursor-pointer transition ${
                        selectedProductIds.includes(product.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleProductSelection(product.id)}
                    >
                     <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition">
  
  {/* Checkbox */}
  <input
    type="checkbox"
    checked={selectedProductIds.includes(product.id)}
    onChange={() => {}}
    onClick={(e) => e.stopPropagation()}
    className="accent-green-600"
  />

  {/* Image */}
  <img
    src={product.image_url || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
    alt={product.name}
    className="w-14 h-14 rounded-md object-cover border bg-gray-100"
    loading="lazy"
    onError={(e) => {
      e.currentTarget.src = 'https://placehold.co/200x200?text=No+Image';
    }}
  />

  {/* Content */}
  <div className="flex-1 min-w-0">
    <p className="text-sm font-medium text-gray-900 truncate">
      {product.name}
    </p>

    <div className="flex items-center gap-2 text-xs text-gray-600">
      <span className="font-semibold text-gray-800">
        {formatCurrency(product.price)}
      </span>

      {product.category_name && (
        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500">
          {product.category_name}
        </span>
      )}
    </div>
  </div>
</div>

                    </div>
                  ))}
              </div>

              {allProducts.filter(p => selectedCategoryId === 'all' || p.category_id === Number(selectedCategoryId)).length === 0 && (
                <p className="text-center text-gray-500 mt-8">No products found in this category</p>
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-4 sticky bottom-0 bg-white">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddProducts}
                disabled={selectedProductIds.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add {selectedProductIds.length} Product{selectedProductIds.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Preview */}
      {fullScreenPreview && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button className="text-white text-4xl hover:text-gray-300" onClick={() => setFullScreenPreview(false)}>×</button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
            <ProductSection config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAdmin;